import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import Page from 'src/layout/Page';
import './personal.css';
import { useTranslation } from 'react-i18next';
import { VerificationStatus } from 'src/shared/types';
import PhoneVerification from 'pages/Personal/components/PhoneVerification/PhoneVerification';
import SubscriptionsList from 'pages/Personal/components/SubscriptionsList/SubscriptionsList';
import ChildrenList from 'pages/Personal/components/ChildrenList/ChildrenList';
import Info from 'pages/Personal/components/Info/Info';
import {useLoader} from 'src/hooks/useLoader/LoaderContext';
import usePurchasedSubscriptions from 'src/hooks/usePurchasedSubscriptions';

const Personal: React.FC = (): React.ReactElement => {
	const [userInfo, setUserInfo]                                                                       = useState<any>();
	const navigate                                                                                      = useNavigate();
	const {
			  t,
			  i18n,
		  }                                                                                             = useTranslation();
	const [verifiedPhone, setVerifiedPhone]                                                             = useState<boolean>(false);
	const [isSMSSent, setIsSMSSent]                                                                     = useState<boolean>(false);
	const [verificationCode, setVerificationCode]                                                       = useState<string>('');
	const [message, setMessage]                                                                         = useState<string>('');
	const [isPhoneUpdated, setIsPhoneUpdated]                                                           = useState<boolean>(true);
	const [phoneNumber, setPhoneNumber]                                                                 = useState<string | undefined>(undefined);
	const {getUser, sendSMS, verifyPhone, isAuthenticated, updatePhoneNumber, refreshUser, verifyEmail} = useAuth();
	const [activeStep, setActiveStep]                                                                   = useState<number>(0);
	const {setIsLoading}                                                                                = useLoader();

	const {
			  purchasedSubscriptions,
		  } = usePurchasedSubscriptions(undefined, true, true);

	const location = useLocation();

	const [emailVerificationMode, setEmailVerificationMode] = useState(location.pathname.includes('email/verify'));

	useEffect(() => {
		if (location.pathname.includes('email/verify')) {
			const params = new URLSearchParams(location.search);
			const token  = params.get('token');
			if (token) {
				setIsLoading(true);
				verifyEmail(token).then((_response) => {
					if (i18n.language !== 'en') {
						navigate('/התחברות');
					} else {
						navigate('/en/personal');
					}
					setEmailVerificationMode(false);
					setIsLoading(false);
				}).catch((e: any) => {
					console.log(e);
					setIsLoading(false);
					if (i18n.language !== 'en') {
						window.location.href = '/התחברות' as string;
					} else {
						window.location.href = '/en/personal' as string ;
					}
				});
			}
		}
	}, [location.pathname]);

	useEffect(() => {
		document.title = t('pages.personal.title') as string;
	}, [i18n.language]);

	useEffect(() => {
		refreshUser().then(() => {
			const authUser = getUser();
			setPhoneNumber(authUser?.phone!.value);
			if (authUser?.phone!.status === VerificationStatus.VERIFIED) {
				setVerifiedPhone(true);
			}
			setUserInfo(authUser);
		});
	}, []);

	useEffect(() => {
		if (!isAuthenticated()) {
			if (i18n.language !== 'en') {
				navigate('/כְּנִיסָה-לַמַעֲרֶכֶת');
			} else {
				navigate('/en/login');
			}
		}
	}, [isAuthenticated]);


	const handleSendSMS = async (event: BaseSyntheticEvent) => {
		event.preventDefault();
		event.stopPropagation();
		const smsSent = await sendSMS();
		if (smsSent) {
			setIsSMSSent(true);
		}
	};

	const handleChangePhoneNumber = (event: BaseSyntheticEvent) => {
		setPhoneNumber(event.target.value);
		const user = getUser();
		if (user?.phone!.value !== event.target.value) {
			setIsPhoneUpdated(false);
		} else {
			setIsPhoneUpdated(true);
		}
	};

	const handleUpdatePhoneNumber = async () => {
		if (phoneNumber) {
			const result = await updatePhoneNumber(phoneNumber);
			await refreshUser();
			const user = getUser();
			if (user?.phone!.status === VerificationStatus.VERIFIED) {
				setVerifiedPhone(true);
			}
			if (result) {
				setIsPhoneUpdated(true);
			}
		}

	};
	const handleCodeInput = (event: BaseSyntheticEvent) => {
		setVerificationCode(event.target.value);
		setMessage('');
	};

	const handlePhoneVerification = async (event: BaseSyntheticEvent) => {
		event.preventDefault();
		event.stopPropagation();
		const verified = await verifyPhone(verificationCode);
		if (verified === true) {
			setVerifiedPhone(true);
		} else {
			setMessage(verified.message);
		}
	};

	const handleAdditionalSubscription = (e: any) => {
		e.preventDefault();
		if (i18n.language !== 'en') {
			navigate('/רישום');
		} else {
			navigate('/en/subscribe');
		}
	};

	const handleMyTimesheet = (e: any) => {
		e.preventDefault();
		if (i18n.language !== 'en') {
			navigate('/מערכת');
		} else {
			navigate('/en/timesheet');
		}
	};

	const stepTitles: Array<string> = Array.from({length: 3}, (_, i) => `pages.personal.stepTitles.${i}`);

	return (
		userInfo && <Page
			pageClass={'personal-page'}
			pageHeader={`${t('pages.personal.hello')} ${userInfo?.name?.trim() || userInfo?.firstName}!`}>
			{
				emailVerificationMode ? null :
					(verifiedPhone || true ? (<>
							<div className="action-buttons">
								<button
									className={'active'}
									onClick={handleAdditionalSubscription}
								>
									{t('pages.personal.purchaseAdditionalSubscription')}
								</button>
								{purchasedSubscriptions && purchasedSubscriptions.length > 0 && (
									<button
										className={'active'}
										onClick={handleMyTimesheet}
									>
										{t('pages.personal.myTimesheet')}
									</button>
								)}
							</div>
							<div className="step-header">
								{stepTitles.map((step, index) => {
									const isActiveClass = index === activeStep ? 'active' : '';
									const key           = `tabKey-${index}`;
									return (
										<div className={`step ${isActiveClass}`} key={key}
											 onClick={() => setActiveStep(index)}>
											{t(step as any)}
										</div>
									);
								})}
							</div>
							{activeStep === 0 && <SubscriptionsList/>}
							{activeStep === 1 && <ChildrenList/>}
							{activeStep === 2 && <Info/>}
						</>)
						: phoneNumber ? <PhoneVerification
							isSMSSent={isSMSSent}
							handleSendSMS={handleSendSMS}
							handleCodeInput={handleCodeInput}
							handlePhoneVerification={handlePhoneVerification}
							phoneNumber={phoneNumber}
							handleChangePhoneNumber={handleChangePhoneNumber}
							isPhoneUpdated={isPhoneUpdated}
							handleUpdatePhoneNumber={handleUpdatePhoneNumber}
							message={message}
						/> : null)
			}
		</Page>
	);
};

export default Personal;
